import React, { useEffect, useState } from "react";
import {
  ArrowLeft, Plus, Edit2, Trash2, GripVertical, Film, X, Save,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";

// Helper: extract an 11-char YouTube id from any URL format (mirrors backend logic)
const extractYouTubeId = (url) => {
  if (!url) return "";
  const u = url.trim();
  const patterns = [
    /(?:youtube\.com\/watch\?[^#]*?\bv=)([A-Za-z0-9_-]{11})/,
    /youtu\.be\/([A-Za-z0-9_-]{11})/,
    /youtube\.com\/embed\/([A-Za-z0-9_-]{11})/,
    /youtube\.com\/shorts\/([A-Za-z0-9_-]{11})/,
    /youtube\.com\/v\/([A-Za-z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = u.match(p);
    if (m) return m[1];
  }
  if (/^[A-Za-z0-9_-]{11}$/.test(u)) return u;
  return "";
};

const emptyForm = { id: null, title: "", description: "", youtubeUrl: "" };

const AdminProjects = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingOrder, setSavingOrder] = useState(false);

  // Drag-drop
  const [draggingId, setDraggingId] = useState(null);
  const [overId, setOverId] = useState(null);

  // Form state
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/projects");
      setItems(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    load();
  }, []);

  // ── Drag & drop reordering ─────────────────────────────────
  const onDragStart = (e, id) => {
    setDraggingId(id);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", id); } catch (_) {}
  };
  const onDragOver = (e, id) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (id !== overId) setOverId(id);
  };
  const onDragLeave = () => setOverId(null);
  const onDrop = async (e, targetId) => {
    e.preventDefault();
    const sourceId = draggingId;
    setDraggingId(null);
    setOverId(null);
    if (!sourceId || sourceId === targetId) return;
    const next = [...items];
    const fromIdx = next.findIndex((p) => p.id === sourceId);
    const toIdx = next.findIndex((p) => p.id === targetId);
    if (fromIdx === -1 || toIdx === -1) return;
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setItems(next);
    setSavingOrder(true);
    try {
      await api.patch("/projects/reorder", { ids: next.map((p) => p.id) });
    } catch (err) {
      await load();
    } finally {
      setSavingOrder(false);
    }
  };
  const onDragEnd = () => {
    setDraggingId(null);
    setOverId(null);
  };

  // ── Form ──────────────────────────────────────────────────
  const openAdd = () => {
    setForm(emptyForm);
    setFormError("");
    setShowForm(true);
  };
  const openEdit = (p) => {
    setForm({
      id: p.id,
      title: p.title || "",
      description: p.description || "",
      youtubeUrl: p.youtubeUrl || "",
    });
    setFormError("");
    setShowForm(true);
  };
  const closeForm = () => {
    if (submitting) return;
    setShowForm(false);
    setFormError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!form.title.trim()) return setFormError("Title is required");
    if (!extractYouTubeId(form.youtubeUrl)) {
      return setFormError("Please paste a valid YouTube URL");
    }
    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        youtubeUrl: form.youtubeUrl.trim(),
      };
      if (form.id) {
        await api.put(`/projects/${form.id}`, payload);
      } else {
        await api.post("/projects", payload);
      }
      setShowForm(false);
      await load();
    } catch (err) {
      setFormError(err?.response?.data?.detail || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setError("");
    try {
      await api.delete(`/projects/${deleteTarget.id}`);
      setDeleteTarget(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Delete failed");
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const previewId = extractYouTubeId(form.youtubeUrl);

  return (
    <div className="min-h-screen bg-[#121212] pt-24 pb-20" data-testid="admin-projects">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-center gap-4 mb-10">
          <button
            onClick={() => navigate("/admin")}
            className="flex items-center gap-2 text-white/40 hover:text-gold transition-colors text-xs font-body tracking-wider uppercase"
          >
            <ArrowLeft size={13} />
            <span>Dashboard</span>
          </button>
          <div className="h-px flex-1 bg-gold/10" />
        </div>

        <div className="flex items-center gap-3 mb-4">
          <div className="h-px w-8 bg-gold/60" />
          <span className="font-body text-gold text-[0.65rem] font-semibold tracking-[0.4em] uppercase">
            Project Showcase
          </span>
        </div>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <h1 className="font-display font-light text-5xl text-white leading-tight">
            Projects
          </h1>
          <div className="flex items-center gap-4">
            {savingOrder && (
              <span
                className="font-body text-gold/70 text-[0.55rem] tracking-[0.3em] uppercase flex items-center gap-2"
                data-testid="projects-order-saving"
              >
                <span className="w-3 h-3 border border-gold/30 border-t-gold rounded-full animate-spin" />
                Saving order…
              </span>
            )}
            <button
              onClick={openAdd}
              className="btn-gold px-7 py-3.5 text-xs rounded-sm flex items-center gap-3"
              data-testid="admin-project-add"
            >
              <Plus size={14} />
              <span>Add Project</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-sm px-4 py-3 mb-6">
            <p className="font-body text-red-400 text-xs">{error}</p>
          </div>
        )}

        <div className="glass rounded-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-gold/10 flex items-center justify-between">
            <h2 className="font-heading text-sm font-semibold tracking-widest text-white/60 uppercase">
              {items.length} {items.length === 1 ? "Project" : "Projects"} live
            </h2>
            {items.length > 1 && (
              <span className="font-body text-white/25 text-[0.55rem] tracking-[0.3em] uppercase">
                Drag rows to reorder
              </span>
            )}
          </div>

          <div className="divide-y divide-white/5">
            {loading ? (
              <div className="py-16 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-gold/20 border-t-gold rounded-full animate-spin" />
              </div>
            ) : items.length === 0 ? (
              <div className="py-16 text-center">
                <Film size={40} className="text-white/10 mx-auto mb-4" />
                <p className="font-body text-white/20 text-sm mb-4">
                  No projects yet. Add your first project.
                </p>
                <button
                  onClick={openAdd}
                  className="btn-outline-gold px-6 py-3 text-xs rounded-sm inline-flex items-center gap-2"
                >
                  <Plus size={13} />
                  <span>Add Project</span>
                </button>
              </div>
            ) : (
              items.map((p, idx) => (
                <div
                  key={p.id}
                  draggable
                  onDragStart={(e) => onDragStart(e, p.id)}
                  onDragOver={(e) => onDragOver(e, p.id)}
                  onDragLeave={onDragLeave}
                  onDrop={(e) => onDrop(e, p.id)}
                  onDragEnd={onDragEnd}
                  className={`flex items-center gap-4 px-6 py-4 hover:bg-white/[0.02] transition-colors group select-none ${
                    draggingId === p.id ? "opacity-40" : ""
                  } ${overId === p.id && draggingId && draggingId !== p.id ? "bg-gold/5 ring-1 ring-inset ring-gold/40" : ""}`}
                  data-testid={`admin-project-row-${p.id}`}
                >
                  <div
                    className="cursor-grab active:cursor-grabbing text-white/25 hover:text-gold transition-colors flex-shrink-0"
                    title="Drag to reorder"
                  >
                    <GripVertical size={16} />
                  </div>
                  <span className="font-body text-gold/50 text-[0.6rem] tracking-[0.3em] uppercase w-6 flex-shrink-0">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  {/* Thumbnail from YouTube */}
                  <div
                    className="relative w-24 h-14 rounded-sm overflow-hidden flex-shrink-0 bg-black border border-gold/15"
                  >
                    <img
                      src={`https://img.youtube.com/vi/${p.youtubeId}/mqdefault.jpg`}
                      alt={p.title}
                      className="w-full h-full object-cover"
                      draggable={false}
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display text-lg text-white truncate">{p.title}</h3>
                    {p.description && (
                      <p className="font-body text-white/35 text-xs truncate">
                        {p.description}
                      </p>
                    )}
                    <a
                      href={p.youtubeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-body text-white/30 hover:text-gold text-[0.6rem] tracking-wider truncate block transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {p.youtubeUrl}
                    </a>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => openEdit(p)}
                      className="w-8 h-8 flex items-center justify-center rounded-sm border border-white/10 hover:border-gold/40 hover:text-gold transition-all text-white/40"
                      title="Edit"
                      data-testid={`admin-project-edit-${p.id}`}
                    >
                      <Edit2 size={13} />
                    </button>
                    <button
                      onClick={() => setDeleteTarget(p)}
                      className="w-8 h-8 flex items-center justify-center rounded-sm border border-white/10 hover:border-red-500/50 hover:text-red-400 transition-all text-white/40"
                      title="Delete"
                      data-testid={`admin-project-delete-${p.id}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Add / Edit modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" data-testid="admin-project-form-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={closeForm} />
          <div className="relative glass-dark rounded-sm p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="font-body text-gold text-[0.6rem] tracking-[0.4em] uppercase mb-2">
                  {form.id ? "Edit Project" : "New Project"}
                </div>
                <h3 className="font-display text-3xl text-white">
                  {form.id ? "Update Project" : "Add a Project"}
                </h3>
              </div>
              <button
                onClick={closeForm}
                className="text-white/40 hover:text-gold transition-colors"
                aria-label="Close"
                data-testid="admin-project-form-close"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="font-body text-white/40 text-[0.65rem] tracking-[0.2em] uppercase block mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Stage Production · Vol III"
                  className="input-gold w-full px-4 py-3.5 rounded-sm text-sm font-body"
                  required
                  data-testid="admin-project-title"
                />
              </div>

              <div>
                <label className="font-body text-white/40 text-[0.65rem] tracking-[0.2em] uppercase block mb-2">
                  YouTube URL *
                </label>
                <input
                  type="url"
                  value={form.youtubeUrl}
                  onChange={(e) => setForm({ ...form, youtubeUrl: e.target.value })}
                  placeholder="https://youtu.be/... or https://youtube.com/watch?v=..."
                  className="input-gold w-full px-4 py-3.5 rounded-sm text-sm font-body"
                  required
                  data-testid="admin-project-url"
                />
                <p className="font-body text-white/25 text-[0.65rem] mt-2">
                  Accepts any YouTube URL format — youtu.be, watch?v=, embed, shorts.
                </p>
              </div>

              {/* Live preview */}
              {previewId && (
                <div>
                  <label className="font-body text-white/40 text-[0.65rem] tracking-[0.2em] uppercase block mb-2">
                    Preview
                  </label>
                  <div
                    className="relative w-full overflow-hidden rounded-sm bg-black border border-gold/20"
                    style={{ aspectRatio: "16 / 9" }}
                  >
                    <iframe
                      src={`https://www.youtube.com/embed/${previewId}?rel=0&modestbranding=1`}
                      title="Preview"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      className="absolute inset-0 w-full h-full"
                      data-testid="admin-project-preview"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="font-body text-white/40 text-[0.65rem] tracking-[0.2em] uppercase block mb-2">
                  Description (optional)
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Short description of the project, venue, or production…"
                  rows={3}
                  className="input-gold w-full px-4 py-3.5 rounded-sm text-sm font-body resize-none"
                  data-testid="admin-project-description"
                />
              </div>

              {formError && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-sm px-4 py-3" data-testid="admin-project-form-error">
                  <p className="font-body text-red-400 text-xs">{formError}</p>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-gold flex-1 py-3.5 text-xs rounded-sm flex items-center justify-center gap-3 disabled:opacity-60 disabled:cursor-wait"
                  data-testid="admin-project-form-submit"
                >
                  {submitting ? (
                    <>
                      <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                      <span>Saving…</span>
                    </>
                  ) : (
                    <>
                      <Save size={13} />
                      <span>{form.id ? "Save Changes" : "Add Project"}</span>
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={closeForm}
                  disabled={submitting}
                  className="btn-outline-gold py-3.5 px-6 text-xs rounded-sm disabled:opacity-60"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" data-testid="admin-project-delete-modal">
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => !deleting && setDeleteTarget(null)}
          />
          <div className="relative glass-dark rounded-sm p-8 max-w-sm w-full">
            <h3 className="font-display text-2xl text-white mb-3">Confirm Delete</h3>
            <p className="font-body text-white/50 text-sm mb-6">
              Remove <span className="text-gold">{deleteTarget.title}</span> from
              the projects page? This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="flex-1 py-3 text-xs rounded-sm font-body font-semibold tracking-widest uppercase bg-red-500/20 border border-red-500/40 text-red-400 hover:bg-red-500/30 transition-colors disabled:opacity-60 disabled:cursor-wait flex items-center justify-center gap-2"
                data-testid="admin-project-delete-confirm"
              >
                {deleting ? (
                  <>
                    <span className="w-3 h-3 border border-red-400/40 border-t-red-400 rounded-full animate-spin" />
                    <span>Deleting…</span>
                  </>
                ) : (
                  <span>Delete</span>
                )}
              </button>
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="flex-1 btn-outline-gold py-3 text-xs rounded-sm disabled:opacity-60"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProjects;
